import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";
import PieChart from "examples/Charts/PieChart";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { GetProfStatsCall } from "../../redux/api/report.api";
import { GetUniversityStudentsCall } from "../../redux/api/student.api";
import { useMaterialUIController } from "context";
import ProfSelector from "components/ProfSelector";
import AdminStatsMap from "prof/stats/AdminStatsMap";
import StatsAccordionSection from "prof/stats/StatsAccordionSection";
import MapIcon from "@mui/icons-material/Map";

const emptyChart = { labels: [], datasets: { label: "", data: [] } };

function normalizeRole(role) {
  return String(role || "")
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");
}

function sumChartData(charts, fallbackLabel = "") {
  const withLabels = (charts || []).filter((c) => c?.labels?.length);
  if (!withLabels.length) return emptyChart;
  const labels = withLabels[0].labels;
  const data = labels.map((_, i) =>
    withLabels.reduce((sum, c) => sum + (Number(c?.datasets?.data?.[i]) || 0), 0)
  );
  return {
    labels,
    datasets: {
      label: withLabels[0]?.datasets?.label || fallbackLabel,
      data,
    },
  };
}

function mergeQuizSummaries(bags) {
  const map = new Map();
  bags.forEach((bag) => {
    (Array.isArray(bag?.quizSummary) ? bag.quizSummary : []).forEach((q) => {
      const key = String(q.quiz_assignment_id || q.title || "");
      if (!key) return;
      const prev = map.get(key);
      if (!prev) {
        map.set(key, {
          ...q,
          attempt_count: Number(q.attempt_count) || 0,
          _scoreSum: q.avg_score != null ? Number(q.avg_score) * (Number(q.attempt_count) || 1) : 0,
          _scoreWeight: q.avg_score != null ? Number(q.attempt_count) || 1 : 0,
        });
        return;
      }
      prev.attempt_count += Number(q.attempt_count) || 0;
      if (q.avg_score != null) {
        const w = Number(q.attempt_count) || 1;
        prev._scoreSum += Number(q.avg_score) * w;
        prev._scoreWeight += w;
      }
    });
  });
  return Array.from(map.values()).map((q) => ({
    quiz_assignment_id: q.quiz_assignment_id,
    title: q.title,
    attempt_count: q.attempt_count,
    avg_score:
      q._scoreWeight > 0 ? Number((q._scoreSum / q._scoreWeight).toFixed(2)) : null,
  }));
}

function mergeHeatmaps(bags) {
  const heatmaps = bags
    .map((b) => b?.attendanceHeatmap)
    .filter((h) => Array.isArray(h) && h.length);
  if (!heatmaps.length) return [];
  const weeks = heatmaps[0].length;
  return Array.from({ length: weeks }, (_, wi) => {
    const days = heatmaps[0][wi] || [];
    return days.map((day, di) => ({
      ...day,
      count: heatmaps.reduce(
        (sum, hm) => sum + (Number(hm?.[wi]?.[di]?.count) || 0),
        0
      ),
    }));
  });
}

function mergeTopStudents(bags) {
  const map = new Map();
  bags.forEach((bag) => {
    (Array.isArray(bag?.topStudentsByScore) ? bag.topStudentsByScore : []).forEach((s) => {
      const key = String(s.user_id || s.name || "");
      if (!key) return;
      const prev = map.get(key);
      if (!prev) {
        map.set(key, {
          ...s,
          attempt_count: Number(s.attempt_count) || 0,
          _scoreSum: s.avg_score != null ? Number(s.avg_score) * (Number(s.attempt_count) || 1) : 0,
          _scoreWeight: s.avg_score != null ? Number(s.attempt_count) || 1 : 0,
        });
        return;
      }
      prev.attempt_count += Number(s.attempt_count) || 0;
      if (s.avg_score != null) {
        const w = Number(s.attempt_count) || 1;
        prev._scoreSum += Number(s.avg_score) * w;
        prev._scoreWeight += w;
      }
    });
  });
  return Array.from(map.values())
    .map((s) => ({
      user_id: s.user_id,
      name: s.name,
      attempt_count: s.attempt_count,
      avg_score:
        s._scoreWeight > 0 ? Number((s._scoreSum / s._scoreWeight).toFixed(1)) : null,
    }))
    .sort((a, b) => (b.avg_score ?? -1) - (a.avg_score ?? -1))
    .slice(0, 10);
}

function mergeProfStatsBags(bags, enrolledStudentsCount = null) {
  const list = (bags || []).filter(Boolean);
  if (!list.length) {
    return {
      studentsCount: enrolledStudentsCount ?? 0,
      attemptsLast7Days: emptyChart,
      quizSummary: [],
      attendanceHeatmap: [],
      attemptsByWeek: emptyChart,
      scoreDistribution: emptyChart,
      activityByDayOfWeek: emptyChart,
      topStudentsByScore: [],
      attemptsByQuizForPie: emptyChart,
    };
  }
  const quizSummary = mergeQuizSummaries(list);
  return {
    studentsCount:
      enrolledStudentsCount != null
        ? enrolledStudentsCount
        : list.reduce((s, b) => s + (Number(b.studentsCount) || 0), 0),
    attemptsLast7Days: sumChartData(
      list.map((b) => b.attemptsLast7Days),
      "Quiz attempts"
    ),
    quizSummary,
    attendanceHeatmap: mergeHeatmaps(list),
    attemptsByWeek: sumChartData(list.map((b) => b.attemptsByWeek), "Attempts"),
    scoreDistribution: sumChartData(
      list.map((b) => b.scoreDistribution),
      "Attempts"
    ),
    activityByDayOfWeek: sumChartData(
      list.map((b) => b.activityByDayOfWeek),
      "Attempts"
    ),
    topStudentsByScore: mergeTopStudents(list),
    attemptsByQuizForPie: {
      labels: quizSummary.map((q) =>
        q.title && q.title.length > 12 ? q.title.slice(0, 11) + "…" : q.title || "Quiz"
      ),
      datasets: {
        label: "Attempts",
        data: quizSummary.map((q) => q.attempt_count || 0),
      },
    },
    universityStorage: list.find((b) => b.universityStorage)?.universityStorage || null,
    myStorage: list.find((b) => b.myStorage)?.myStorage || null,
  };
}

const LAST_7_DAYS_LABELS = [];
for (let i = 6; i >= 0; i--) {
  const d = new Date();
  d.setDate(d.getDate() - i);
  LAST_7_DAYS_LABELS.push(d.toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric" }));
}
const PLACEHOLDER_7_DAYS = { labels: LAST_7_DAYS_LABELS, datasets: { label: "Quiz attempts", data: [0, 0, 0, 0, 0, 0, 0] } };

const PLACEHOLDER_WEEK_LABELS = Array.from({ length: 8 }, (_, i) => {
  const d = new Date();
  d.setDate(d.getDate() - (7 - i) * 7);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
});
const PLACEHOLDER_BY_WEEK = { labels: PLACEHOLDER_WEEK_LABELS, datasets: { label: "Attempts", data: Array(8).fill(0) } };

const DAY_OF_WEEK_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const PLACEHOLDER_BY_DOW = { labels: DAY_OF_WEEK_LABELS, datasets: { label: "Attempts", data: Array(7).fill(0) } };

const SCORE_BANDS = ["0-20%", "20-40%", "40-60%", "60-80%", "80-100%"];
const PLACEHOLDER_SCORE_DIST = { labels: SCORE_BANDS, datasets: { label: "Attempts", data: [0, 0, 0, 0, 0] } };

function AttendanceHeatmap({ attendanceHeatmap }) {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  if (!Array.isArray(attendanceHeatmap) || attendanceHeatmap.length === 0) {
    return (
      <MDTypography variant="body2" color="text">
        {t("pages.stats.noActivityDataYet")}
      </MDTypography>
    );
  }
  // One row: each square = one week (sum of that week's days)
  const weekData = attendanceHeatmap.map((weekDays) => {
    const count = weekDays.reduce((s, d) => s + (d.count || 0), 0);
    const firstDay = weekDays[0];
    const weekLabel = firstDay?.label ? `Week of ${firstDay.label}` : "";
    return { count, weekLabel };
  });
  const maxCount = Math.max(1, ...weekData.map((w) => w.count));
  const bg = darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
  return (
    <MDBox>
      <MDTypography variant="caption" color="text" display="block" mb={1}>
        {t("pages.stats.studentAppUsageDesc")}
      </MDTypography>
      <MDBox
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${weekData.length}, 1fr)`,
          gap: 1,
          maxWidth: "100%",
        }}
      >
        {weekData.map((week, i) => {
          const pct = maxCount > 0 ? week.count / maxCount : 0;
          const bgColor =
            week.count === 0
              ? bg
              : `rgba(33, 150, 243, ${0.25 + 0.65 * pct})`;
          return (
            <MDBox
              key={`week-${i}`}
              title={`${week.weekLabel}: ${week.count} attempt(s)`}
              sx={{
                aspectRatio: "1",
                borderRadius: 1,
                bgcolor: bgColor,
                minWidth: 28,
                minHeight: 28,
              }}
            />
          );
        })}
      </MDBox>
      <MDTypography variant="caption" color="text" display="block" mt={0.5}>
        {t("pages.stats.last6Weeks")}
      </MDTypography>
    </MDBox>
  );
}

export default function ProfStats({ noLayout = false }) {
  const { t } = useTranslation();
  const auth = useSelector((state) => state?.auth || {});
  const role = normalizeRole(auth?.role);
  const authUserId = auth?.id || auth?.user_id || null;
  const authUniversityId =
    auth?.university_id ??
    auth?.universityInfo?.id ??
    auth?.universityInfo?.university_id ??
    null;
  const canSelectProf =
    role === "admin" || role === "university_admin" || role === "university";
  const isUniAdmin = role === "university_admin" || role === "university";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProfId, setSelectedProfId] = useState("");
  const [profOptions, setProfOptions] = useState([]);
  const [profsLoaded, setProfsLoaded] = useState(!canSelectProf);

  const allProfIdsForScope = useMemo(() => {
    if (selectedProfId) return [String(selectedProfId)];
    if (!canSelectProf) return authUserId ? [String(authUserId)] : [];
    const ids = profOptions.map((p) => p.id).filter(Boolean);
    if (authUserId) ids.push(authUserId);
    return Array.from(new Set(ids.map(String)));
  }, [selectedProfId, canSelectProf, profOptions, authUserId]);

  useEffect(() => {
    let mounted = true;

    // Wait for professor dropdown to finish loading before aggregating "All".
    if (canSelectProf && !selectedProfId && !profsLoaded) {
      setLoading(true);
      return undefined;
    }

    setLoading(true);

    const load = async () => {
      try {
        // Single professor selected — one API call.
        if (selectedProfId) {
          const res = await GetProfStatsCall(selectedProfId);
          if (mounted && res != null) setData(res);
          return;
        }

        // Uni admin / admin "All": aggregate each professor's stats + enrolled roster count.
        if (canSelectProf) {
          const [bags, students] = await Promise.all([
            allProfIdsForScope.length
              ? Promise.all(allProfIdsForScope.map((id) => GetProfStatsCall(id)))
              : Promise.resolve([]),
            isUniAdmin && authUniversityId
              ? GetUniversityStudentsCall(authUniversityId)
              : Promise.resolve(null),
          ]);
          const enrolled = Array.isArray(students) ? students.length : null;
          if (mounted) setData(mergeProfStatsBags(bags, enrolled));
          return;
        }

        // Plain professor: own stats.
        const res = await GetProfStatsCall(null);
        if (mounted && res != null) setData(res);
      } catch (_) {
        if (mounted) setData(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [
    selectedProfId,
    canSelectProf,
    isUniAdmin,
    authUniversityId,
    profsLoaded,
    allProfIdsForScope.join("|"),
  ]);

  const studentsCount = data?.studentsCount ?? 0;
  const rawAttemptsLast7 = data?.attemptsLast7Days ?? emptyChart;
  const attemptsLast7Days =
    rawAttemptsLast7?.labels?.length > 0 ? rawAttemptsLast7 : PLACEHOLDER_7_DAYS;
  const quizSummary = Array.isArray(data?.quizSummary) ? data.quizSummary : [];
  const attendanceHeatmap = Array.isArray(data?.attendanceHeatmap) ? data.attendanceHeatmap : [];
  const rawByWeek = data?.attemptsByWeek ?? emptyChart;
  const attemptsByWeek = rawByWeek?.labels?.length > 0 ? rawByWeek : PLACEHOLDER_BY_WEEK;
  const rawScoreDist = data?.scoreDistribution ?? emptyChart;
  const scoreDistribution =
    rawScoreDist?.labels?.length > 0 ? rawScoreDist : PLACEHOLDER_SCORE_DIST;
  const rawByDOW = data?.activityByDayOfWeek ?? emptyChart;
  const activityByDayOfWeek = rawByDOW?.labels?.length > 0 ? rawByDOW : PLACEHOLDER_BY_DOW;
  const topStudentsByScore = Array.isArray(data?.topStudentsByScore) ? data.topStudentsByScore : [];
  const attemptsByQuizForPie = data?.attemptsByQuizForPie ?? emptyChart;

  const totalAttempts = quizSummary.reduce((s, q) => s + (q.attempt_count || 0), 0);
  const avgScoreList = quizSummary.filter((q) => q.avg_score != null);
  const overallAvgScore =
    avgScoreList.length > 0
      ? (avgScoreList.reduce((s, q) => s + q.avg_score, 0) / avgScoreList.length).toFixed(1)
      : null;

  const quizScoreChart = {
    labels: quizSummary.map((q) => (q.title && q.title.length > 15 ? q.title.slice(0, 14) + "…" : q.title || "Quiz")),
    datasets: {
      label: "Avg score",
      data: quizSummary.map((q) => q.avg_score ?? 0),
    },
  };

  const quizAttemptsChart = {
    labels: quizSummary.map((q) => (q.title && q.title.length > 15 ? q.title.slice(0, 14) + "…" : q.title || "Quiz")),
    datasets: {
      label: "Attempts",
      data: quizSummary.map((q) => q.attempt_count ?? 0),
    },
  };

  const topStudentsByScoreChart = {
    labels: topStudentsByScore.map((s) => (s.name && s.name.length > 18 ? s.name.slice(0, 17) + "…" : s.name || "—")),
    datasets: [{ data: topStudentsByScore.map((s) => s.avg_score ?? 0), color: "success" }],
  };

  const PIE_COLORS = ["info", "success", "dark", "primary", "secondary", "warning", "error"];
  const attemptsByQuizPieWithColors = attemptsByQuizForPie?.labels?.length
    ? {
        ...attemptsByQuizForPie,
        datasets: {
          ...attemptsByQuizForPie.datasets,
          backgroundColors: (attemptsByQuizForPie.datasets?.data || []).map((_, i) => PIE_COLORS[i % PIE_COLORS.length]),
        },
      }
    : emptyChart;
  const scoreDistDoughnutColors = scoreDistribution?.datasets?.data
    ? { ...scoreDistribution, datasets: { ...scoreDistribution.datasets, backgroundColors: ["error", "warning", "secondary", "info", "success"] } }
    : emptyChart;

  const loadingContent = (
    <MDBox py={3}>
      <MDTypography>{t("pages.stats.loadingStats")}</MDTypography>
    </MDBox>
  );

  if (loading) {
    if (noLayout) return loadingContent;
    return (
      <DashboardLayout>
        <DashboardNavbar />
        {loadingContent}
      </DashboardLayout>
    );
  }

  const mainContent = (
    <MDBox py={3}>
        <MDBox mb={3} display="flex" alignItems="center" flexWrap="wrap" gap={2}>
          <ProfSelector
            value={selectedProfId}
            onChange={setSelectedProfId}
            emptyLabel="All"
            onProfsLoaded={(list) => {
              setProfOptions(Array.isArray(list) ? list : []);
              setProfsLoaded(true);
            }}
          />
        </MDBox>
        <MDBox
          sx={{
            display: "grid",
            // Content-width aware: AI panel can shrink the main column while
            // the viewport still reports "lg", which used to squash 4 cards.
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
            gap: 3,
            width: "100%",
            // Room for ComplexStatisticsCard icons that sit above the card.
            pt: 2,
            alignItems: "stretch",
          }}
        >
          <MDBox sx={{ minWidth: 0, height: "100%", "& > .MuiCard-root": { height: "100%" } }}>
            <ComplexStatisticsCard
              color="info"
              icon="groups"
              title={t("pages.stats.myStudents")}
              count={studentsCount}
              percentage={{ color: "success", label: t("pages.stats.sameUniversity") }}
            />
          </MDBox>
          <MDBox sx={{ minWidth: 0, height: "100%", "& > .MuiCard-root": { height: "100%" } }}>
            <ComplexStatisticsCard
              color="success"
              icon="quiz"
              title={t("pages.stats.quizAttempts")}
              count={totalAttempts}
              percentage={{ color: "success", label: t("pages.stats.allTimeMyStudents") }}
            />
          </MDBox>
          <MDBox sx={{ minWidth: 0, height: "100%", "& > .MuiCard-root": { height: "100%" } }}>
            <ComplexStatisticsCard
              color="dark"
              icon="assignment"
              title={t("pages.stats.assignments")}
              count={quizSummary.length}
              percentage={{ color: "success", label: t("pages.stats.withAttempts") }}
            />
          </MDBox>
          <MDBox sx={{ minWidth: 0, height: "100%", "& > .MuiCard-root": { height: "100%" } }}>
            <ComplexStatisticsCard
              color="secondary"
              icon="trending_up"
              title={t("pages.stats.avgScore")}
              count={overallAvgScore != null ? `${overallAvgScore}%` : "—"}
              percentage={{ color: "success", label: t("pages.stats.acrossQuizzes") }}
            />
          </MDBox>
        </MDBox>

        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="info"
                  title={t("pages.stats.quizAttemptsLast7Days")}
                  description={t("pages.stats.submissionsByStudents")}
                  date=""
                  chart={attemptsLast7Days}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3}>
                <ReportsLineChart
                  color="primary"
                  title={t("pages.stats.quizAttemptsPerWeek")}
                  description={t("pages.stats.last8Weeks")}
                  date=""
                  chart={attemptsByWeek}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              {quizSummary.length > 0 ? (
                <MDBox mb={3}>
                  <ReportsLineChart
                    color="success"
                    title={t("pages.stats.avgScorePerQuiz")}
                    description={t("pages.stats.byAssignmentStudents")}
                    date=""
                    chart={quizScoreChart}
                  />
                </MDBox>
              ) : (
                <MDBox mb={3}>
                  <ReportsLineChart
                    color="success"
                    title={t("pages.stats.avgScorePerQuiz")}
                    description={t("pages.stats.noAssignmentsYet")}
                    date=""
                    chart={{ labels: ["—"], datasets: { label: "Avg score", data: [0] } }}
                  />
                </MDBox>
              )}
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              {quizSummary.length > 0 ? (
                <MDBox mb={3}>
                  <ReportsBarChart
                    color="dark"
                    title={t("pages.stats.attemptsPerQuiz")}
                    description={t("pages.stats.totalSubmissionsByAssignment")}
                    date=""
                    chart={quizAttemptsChart}
                  />
                </MDBox>
              ) : (
                <MDBox mb={3}>
                  <ReportsBarChart
                    color="dark"
                    title={t("pages.stats.attemptsPerQuiz")}
                    description={t("pages.stats.noAttemptsYet")}
                    date=""
                    chart={{ labels: ["—"], datasets: { label: "Attempts", data: [0] } }}
                  />
                </MDBox>
              )}
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="secondary"
                  title={t("pages.stats.activityByDayOfWeek")}
                  description={t("pages.stats.whenStudentsSubmit")}
                  date=""
                  chart={activityByDayOfWeek}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <Card sx={{ height: "100%", p: 2 }}>
                <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                  {t("pages.stats.studentActivityHeatmap")}
                </MDTypography>
                <AttendanceHeatmap attendanceHeatmap={attendanceHeatmap} />
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ReportsBarChart
                  color="warning"
                  title={t("pages.stats.scoreDistribution")}
                  description={t("pages.stats.attemptsByScoreRange")}
                  date=""
                  chart={scoreDistribution}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <DefaultDoughnutChart
                  icon={{ color: "warning", component: "" }}
                  title={t("pages.stats.scoreDistribution")}
                  description={t("pages.stats.shareByScoreBand")}
                  height="16rem"
                  chart={scoreDistDoughnutColors}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              {attemptsByQuizForPie?.labels?.length > 0 && attemptsByQuizForPie.datasets?.data?.some((v) => v > 0) ? (
                <MDBox mb={3}>
                  <PieChart
                    icon={{ color: "info", component: "" }}
                    title={t("pages.stats.attemptsByQuiz")}
                    description={t("pages.stats.sharePerAssignment")}
                    height="16rem"
                    chart={attemptsByQuizPieWithColors}
                  />
                </MDBox>
              ) : (
                <MDBox mb={3}>
                  <PieChart
                    icon={{ color: "info", component: "" }}
                    title={t("pages.stats.attemptsByQuiz")}
                    description={t("pages.stats.noAttemptsYet")}
                    height="16rem"
                    chart={{ labels: ["No data"], datasets: { label: "Attempts", data: [1], backgroundColors: ["info"] } }}
                  />
                </MDBox>
              )}
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <MDBox mb={3}>
                <HorizontalBarChart
                  icon={{ color: "success", component: "" }}
                  title={t("pages.stats.topStudentsByScore")}
                  description={t("pages.stats.bestAvgScoreDesc")}
                  height="20rem"
                  chart={
                    topStudentsByScore.length > 0
                      ? topStudentsByScoreChart
                      : { labels: ["—"], datasets: [{ data: [0], color: "success" }] }
                  }
                />
              </MDBox>
            </Grid>
            <Grid item xs={12}>
              <StatsAccordionSection
                id="students-map"
                title={t("pages.stats.studentsMap", "Students by country")}
                subtitle={t(
                  "pages.stats.studentsMapSubtitle",
                  "Where your university students are located — based on profile country"
                )}
                icon={<MapIcon fontSize="small" />}
                defaultExpanded
              >
                <AdminStatsMap scoped />
              </StatsAccordionSection>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
  );

  if (noLayout) return mainContent;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {mainContent}
      <Footer />
    </DashboardLayout>
  );
}
